/**
 * Displays content provided only in Sitecore's Experience Editor,
 * and in a visually consistent frame that can be expanded/collapsed,
 * dismissed (hidden completely) and given a title.
 *
 * The intention of this component is to provide components a means
 * to provide/surface other JSS fields for an author to see/author, when
 * otherwise it might be hidden (e.g. for anything conditionally rendered).
 */

import React, { useState } from 'react';
import { Text } from '@sitecore-jss/sitecore-jss-react';

import ExperienceEditorContext from 'core/sitecore/ExperienceEditorContext';
import ExperienceEditorButton from 'core/sitecore/experience-editor/ExperienceEditorButton';
import { Icon, ICONS } from 'core/components/Icon';
import Collapse from 'core/components/Collapse';
import { JssTextType } from 'core/utils/sitecore-utils';
import * as GS from 'core/styles/Containers';

import * as S from './ExperienceEditorContent.style';
import CONTENT from './ExperienceEditorContent.content';

interface IProps {
	children?: React.ReactNode;
	title?: string;
	margin?: string;
	startCollapsed?: boolean;
	elementDefinitions?: { name: string; jss: JssTextType }[];
}

const ExperienceEditorContent = ({
	margin,
	children,
	elementDefinitions,
	startCollapsed = false,
	title = CONTENT.DEFAULT_TEXT,
}: IProps) => {
	// 'Dismiss'; two state variables so we can animate close and then 'display: none'
	const [isVisuallyHidden, setIsVisuallyHidden] = useState(false);
	const [isActuallyHidden, setIsActuallyHidden] = useState(false);

	// Toggle collapse content
	const [isCollapsed, setIsCollapsed] = useState(startCollapsed);

	return (
		<ExperienceEditorContext.Consumer>
			{(isEE: boolean) =>
				isEE && (
					<Collapse
						isOpen={!isVisuallyHidden}
						onCloseEnd={() => setIsActuallyHidden(true)}
					>
						<S.Wrapper {...{ margin, hidden: isActuallyHidden }}>
							<S.Header>
								<S.Notice title={CONTENT.HOVER_HELP_TEXT}>
									<Icon
										style={{ marginRight: '2px' }}
										size={16}
										name={ICONS.VISIBILITY_OFF}
									/>
									{title}
								</S.Notice>
								<S.Controls>
									{isCollapsed && (
										<S.IsCollapsed>{CONTENT.IS_COLLAPSED}</S.IsCollapsed>
									)}
									<ExperienceEditorButton
										StyledComponent={S.ToggleCollapse}
										type="button"
										callback={() => setIsCollapsed(c => !c)}
										aria-label={CONTENT.BUTTONS.TOGGLE_COLLAPSE}
										title={CONTENT.BUTTONS.TOGGLE_COLLAPSE}
									>
										<Icon
											name={ICONS.CHEVRON_RIGHT}
											rotate={isCollapsed ? 90 : 270}
											size={25}
										/>
									</ExperienceEditorButton>
									<ExperienceEditorButton
										StyledComponent={S.Dismiss}
										type="button"
										callback={() => setIsVisuallyHidden(true)}
										aria-label={CONTENT.BUTTONS.DISMISS}
										title={CONTENT.BUTTONS.DISMISS}
									>
										<Icon name={ICONS.CLOSE} />
									</ExperienceEditorButton>
								</S.Controls>
							</S.Header>
							<Collapse isOpen={!isCollapsed}>
								<S.Content>
									{elementDefinitions && (
										<GS.EEFieldDefinitionList>
											{elementDefinitions?.map(({ name, jss }) => (
												<React.Fragment key={name}>
													<dt>{name}:</dt>
													<dd>
														<Text field={jss} />
													</dd>
												</React.Fragment>
											))}
										</GS.EEFieldDefinitionList>
									)}
									{children}
								</S.Content>
							</Collapse>
						</S.Wrapper>
					</Collapse>
				)
			}
		</ExperienceEditorContext.Consumer>
	);
};

export default ExperienceEditorContent;
